import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api, useApi } from "../api.ts";
import { useAuth, useMe } from "../auth.tsx";
import { AuthLayout } from "../layouts.tsx";
import { Button, ErrorText, Loading, useToast } from "../ui/index.tsx";
import { DiscordGlyph } from "./Account.tsx";
import type { DiscordLinkPreview, Me } from "../../shared/types.ts";

/** Landing page for the /link button the Discord bot sends. Shown only to signed-in users. */
export default function LinkDiscord() {
  const { code = "" } = useParams();
  const me = useMe();
  const { setMe } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const preview = useApi<DiscordLinkPreview>(`/discord/link/${encodeURIComponent(code)}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      setMe(await api.post<Me>(`/discord/link/${encodeURIComponent(code)}`));
      toast("Discord linked. Your roles will appear in a few seconds.", "success");
      navigate("/account");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Link your Discord" subtitle="Connect Discord to your ExoWare account to get your game roles.">
      {preview.loading && !preview.data ? <Loading /> : preview.error ? (
        <div className="space-y-4">
          <ErrorText>{preview.error.message}</ErrorText>
          <Link to="/account"><Button className="w-full">Back to your account</Button></Link>
        </div>
      ) : preview.data && (
        <div className="space-y-5">
          <div className="flex items-center gap-3 rounded-xl border border-line bg-bg/60 p-4">
            <div className="grid size-11 place-items-center rounded-xl bg-[#5865F2] text-white shadow-[0_0_24px_-6px_#5865F2]"><DiscordGlyph /></div>
            <div className="min-w-0">
              <div className="kicker">discord</div>
              <div className="truncate font-medium">{preview.data.discord_name}</div>
            </div>
            <span className="mx-1 text-faint">→</span>
            <div className="min-w-0">
              <div className="kicker">exoware</div>
              <div className="truncate font-medium">{me.username}</div>
            </div>
          </div>
          {me.discord && <p className="text-sm text-muted">This replaces your current link to <b>{me.discord.name}</b>.</p>}
          {preview.data.already_linked_to_other && <p className="text-sm text-warn">That Discord account is linked to another ExoWare account. Linking it here moves it.</p>}
          <ErrorText>{error}</ErrorText>
          <Button variant="primary" size="lg" className="w-full" loading={busy} onClick={() => void confirm()}>Link accounts</Button>
          <p className="text-center text-xs text-faint">Only continue if you ran /link yourself. The link works once and expires in 15 minutes.</p>
        </div>
      )}
    </AuthLayout>
  );
}
